import { useState } from "react";

import { NotificationExpandToggle } from "./NotificationExpandToggle";
import { Column, Row, Text } from "@/components";
import { AgenticScoreChip } from "@jobs/pipeline/shared/AgenticScoreChip";

import { agenticScoreToBucket } from "@/lib/agenticScore";
import {
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_NOTIFICATION_SURFACES, MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { NotificationStaleCandidaciesPayload, NotificationType } from "@/types";

// Expandable list of a staffer's stale candidacies, grouped by job.
export const NotificationStaleCandidaciesList = ({
  payload,
  trackingSurface,
}: {
  payload: NotificationStaleCandidaciesPayload;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Pipeline order — later stages sort first within a job.
  const stageOrder = Object.keys(candidacyStatusToLabel);
  const jobs = [...payload.jobs]
    .sort((a, b) => b.candidacies.length - a.candidacies.length)
    .map((job) => ({
      ...job,
      candidacies: [...job.candidacies].sort(
        (a, b) => stageOrder.indexOf(b.status) - stageOrder.indexOf(a.status),
      ),
    }));
  const candidacyCount = jobs.reduce(
    (sum, job) => sum + job.candidacies.length,
    0,
  );

  const staleForLabel = (stalledSince: string): string => {
    const days = dates.calculateDaysAgo(stalledSince);
    return `stale for ${String(days)} ${days === 1 ? "day" : "days"}`;
  };

  const render = () => (
    <Column className="gap-0.5">
      <NotificationExpandToggle
        isExpanded={isExpanded}
        notificationType={NotificationType.STALE_CANDIDACIES}
        setIsExpanded={setIsExpanded}
        trackingSurface={trackingSurface}
      >
        {candidacyCount} {candidacyCount === 1 ? "candidate" : "candidates"}
      </NotificationExpandToggle>
      {isExpanded ? (
        <Column className="gap-2 border-l border-slate-200 pl-3 pt-1">
          {jobs.map((job) => (
            <Column key={job.jobId} className="gap-1">
              <Text className="px-2 text-sm font-medium text-slate-900">
                {job.reqTitle} at {job.customerName} ({job.candidacies.length})
              </Text>
              {job.candidacies.map((candidacy) => {
                const bucket = agenticScoreToBucket(candidacy.candidateRating);
                return (
                  <Column
                    key={candidacy.candidacyId}
                    className="cursor-pointer gap-0 rounded-md px-2 py-1.5 hover:bg-slate-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      mixpanelUtils.trackRowClick(
                        "Notification list item",
                        MIXPANEL_SCREENS.NOTIFICATIONS,
                        {
                          notification_type:
                            NotificationType.STALE_CANDIDACIES,
                          surface: trackingSurface,
                        },
                      );
                      window.open(
                        getCandidacyLink({
                          candidacyId: candidacy.candidacyId,
                          companyId: job.companyId,
                          jobId: job.jobId,
                        }),
                        "_blank",
                      );
                    }}
                  >
                    <Row className="items-center gap-1.5">
                      {bucket ? (
                        <AgenticScoreChip
                          bucket={bucket}
                          className="gap-0.5 px-1.5 py-0 text-[10px]"
                        />
                      ) : null}
                      <Text className="text-sm">{candidacy.candidateName}</Text>
                    </Row>
                    <Row className="items-center gap-1">
                      <div
                        className={cn(
                          "h-2 w-2 shrink-0 rounded-full",
                          candidacyStatusToBackgroundColor[candidacy.status],
                        )}
                      />
                      <Text className="text-xs font-normal text-slate-500">
                        {candidacyStatusToLabel[candidacy.status]} ·{" "}
                        {staleForLabel(candidacy.stalledSince)}
                      </Text>
                    </Row>
                  </Column>
                );
              })}
            </Column>
          ))}
        </Column>
      ) : null}
    </Column>
  );

  return render();
};
